import { useCallback, useEffect, useRef, useState } from 'react';
import * as action from '../../actions';
import Claim from '../../components/claim/claim';
import { useHistory, useParams } from 'react-router-dom';
import questionProjections from '../../projections/question';
import usePrevious from '../../hooks/usePrevious';
import questionGroupStepProjections from '../../projections/questionGroupStep';
import claimTypesStates from '../../utils/constants/claimTypesStates';
import { useAppSelector, useAppDispatch } from '../../hooks/hooks';
import { ChangePayload } from '../../actions/answer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import codes from '../../utils/constants/codes';
import applicationSettingsProjections from '../../projections/applicationSettings';

const ClaimContainer = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { i18n } = useTranslation();
    const currentCountryCode = useAppSelector(
        applicationSettingsProjections.getCountry,
    );
    const languageDependency = i18n.language;
    const { refillClaimId }: { refillClaimId: string } = useParams();
    const [confirmationModalOpen, setConfirmationModalOpen] =
        useState<boolean>(false);
    const [hasRouteChangedByModal, setHasRouteChangedByModal] =
        useState<boolean>(false);
    const [storedInputChange, setStoredInputChange] = useState<any>(null);
    const questionsExists = useAppSelector(questionProjections.questionsExists);
    const policyTypes = useAppSelector(
        state => state.policyType.policyType.byId,
    );
    const answers = useAppSelector(state => state.answer.answer);
    const claimCategories = useAppSelector(
        state => state.claimCategory.claimCategory.byId,
    );
    const formTypes = useAppSelector(state => state.formType.formType.byId);
    const fetchingQuestionGroupSteps = useAppSelector(
        state => state.questionGroupStep.loading,
    );
    const arePoliciesLoading = useAppSelector(
        state => state.policyType.loading,
    );
    const policyTypesAttempted = useAppSelector(
        state => state.policyType.attempted,
    );
    const enableAutoSave = useAppSelector(
        state =>
            state.applicationSettings.apiApplicationSettings.enableAutoSave,
    );
    const areClaimCategoriesLoading = useAppSelector(
        state => state.claimCategory.loading,
    );
    const areFormTypesLoading = useAppSelector(state => state.formType.loading);
    const prevFetchingQuestionGroupSteps: any = usePrevious(
        fetchingQuestionGroupSteps,
    );
    const questionGroupStepWithSmallestRank = useAppSelector(
        questionGroupStepProjections.questionGroupStepWithSmallestRank,
    );
    const activeGroupStepCode = useAppSelector(
        state => state.answer.answer.activeGroupStepCode,
    );
    const loadingNoSubmittedDraft: any = useAppSelector(
        state => state.answer.loadingNoSubmittedDraft,
    );
    const [questionGroupsStepsLoading, setQuestionGroupsStepsLoading] =
        useState<boolean>(false);

    const fetchPolicyTypesAction = useCallback(
        (payload: any) => dispatch(action.fetchPolicyTypes(payload)),
        [dispatch],
    );

    // the draft
    const fetchNoSubmittedDraftClaimApplicationByUserAction = useCallback(
        (payload: any) =>
            dispatch(
                action.fetchNoSubmittedDraftClaimApplicationByUser(payload),
            ),
        [dispatch],
    );

    const clearAnswersFromGroupStepsAction = useCallback(
        (payload: action.clearAnswersFromGroupStepsAction | any) =>
            dispatch(action.clearAnswersFromGroupSteps(payload)),
        [dispatch],
    );

    const fetchClaimCategoriesByPolicyTypeAction = useCallback(
        (payload: action.fetchClaimCategoriesByPolicyTypeAction | any) =>
            dispatch(action.fetchClaimCategoriesByPolicyType(payload)),
        [dispatch],
    );
    const fetchQuestionGroupStepByPolicyTypeAndClaimCategoryAndClaimTypeAction =
        useCallback(
            (payload: {
                policyTypeCode: string;
                claimCategoryCode: string;
                formTypeCode: string;
            }) =>
                dispatch(
                    action.fetchQuestionGroupStepByPolicyTypeAndClaimCategoryAndClaimType(
                        payload as any,
                    ),
                ),
            [dispatch],
        );

    const changeAnswerStateAction = useCallback(
        (payload: ChangePayload) =>
            dispatch(action.changeAnswerState(payload as any)),
        [dispatch],
    );

    const clearFormTypesStateAction = useCallback(
        (payload: action.clearFormTypesAction) =>
            dispatch(action.clearFormTypes(payload)),
        [dispatch],
    );

    const clearClaimCategoryStateAction = useCallback(
        (payload: action.clearClaimCategoryStateAction) =>
            dispatch(action.clearClaimCategoryState(payload)),
        [dispatch],
    );

    const clearQuestionStateAction = useCallback(
        (payload: any) => dispatch(action.clearQuestionState(payload)),
        [dispatch],
    );
    const clearAnswerStateAction = useCallback(
        () => dispatch(action.clearAnswersState()),
        [dispatch],
    );

    const fetchDraftClaimApplicationByUserActionAction = useCallback(
        (payload: string | any) =>
            dispatch(action.fetchDraftClaimApplicationByUser(payload)),
        [dispatch],
    );

    const fetchFormTypesByPolicyAndCategoryCodeAction = useCallback(
        (payload: { policyTypeCode: string; claimCategoryCode: string }) =>
            dispatch(
                action.fetchFormTypesByPolicyAndCategoryCode(payload as any),
            ),
        [dispatch],
    );

    useEffect(() => {
        // only if this condition is done, the loading wil be load, and it will depends only of those 2 conditions too.activeTab
        if (!fetchingQuestionGroupSteps && prevFetchingQuestionGroupSteps) {
            setQuestionGroupsStepsLoading(true);
        }
    }, [fetchingQuestionGroupSteps, prevFetchingQuestionGroupSteps]);

    //on initial mount clear questions in order for the form to be auto
    // populated correctly
    useEffect(() => {
        clearQuestionStateAction(undefined);
    }, []);

    // Reference data — fetch once per session. The reducer's `attempted` flag

    useEffect(() => {
        if (policyTypesAttempted || arePoliciesLoading) return;
        fetchPolicyTypesAction(undefined);
    }, [fetchPolicyTypesAction, policyTypesAttempted, arePoliciesLoading]);

    // Fetch the last non-submitted draft claim application.
    const draftFetchInitiatedRef = useRef(false);

    useEffect(() => {
        if (!draftFetchInitiatedRef.current && !loadingNoSubmittedDraft) {
            if (enableAutoSave) {
                if (!refillClaimId) {
                    draftFetchInitiatedRef.current = true;
                    fetchNoSubmittedDraftClaimApplicationByUserAction(
                        undefined,
                    );
                    //if the route is in a group step go to the root
                    history.replace('/claim');
                } else {
                    draftFetchInitiatedRef.current = true;
                    fetchDraftClaimApplicationByUserActionAction(refillClaimId);
                }
            } else if (refillClaimId) {
                draftFetchInitiatedRef.current = true;
                fetchDraftClaimApplicationByUserActionAction(refillClaimId);
            }
        }
    }, [
        fetchNoSubmittedDraftClaimApplicationByUserAction,
        enableAutoSave,
        refillClaimId,
        fetchDraftClaimApplicationByUserActionAction,
        history,
        loadingNoSubmittedDraft,
    ]);

    useEffect(() => {
        if (answers.policyType) {
            fetchClaimCategoriesByPolicyTypeAction({
                policyTypeCode: answers.policyType,
            });
        }
    }, [
        answers.policyType,
        fetchClaimCategoriesByPolicyTypeAction,
        languageDependency,
    ]);

    useEffect(() => {
        if (answers.policyType && answers.claimCategory) {
            fetchFormTypesByPolicyAndCategoryCodeAction({
                policyTypeCode: answers.policyType,
                claimCategoryCode: answers.claimCategory,
            });
        }
    }, [
        answers.policyType,
        answers.claimCategory,
        fetchFormTypesByPolicyAndCategoryCodeAction,
        languageDependency,
    ]);

    useEffect(() => {
        const formTypeNotRequired =
            currentCountryCode === codes.canadaCode &&
            answers.claimCategory &&
            !areFormTypesLoading &&
            Object.keys(formTypes).length === 0;

        if (answers.policyType && (answers.formType || formTypeNotRequired)) {
            fetchQuestionGroupStepByPolicyTypeAndClaimCategoryAndClaimTypeAction(
                {
                    policyTypeCode: answers.policyType,
                    claimCategoryCode: answers.claimCategory,
                    formTypeCode: answers.formType,
                },
            );
        }
    }, [
        answers.policyType,
        answers.claimCategory,
        answers.formType,
        areFormTypesLoading,
        formTypes,
        fetchQuestionGroupStepByPolicyTypeAndClaimCategoryAndClaimTypeAction,
        currentCountryCode,
        languageDependency,
    ]);

    //clear answer state on clean up
    useEffect(() => {
        return () => {
            clearAnswerStateAction();
            clearClaimCategoryStateAction(undefined as any);
            clearFormTypesStateAction(undefined as any);
        };
    }, [
        clearAnswerStateAction,
        clearClaimCategoryStateAction,
        clearFormTypesStateAction,
    ]);

    // upon receiving the new question groups redirect to the first tab
    useEffect(() => {
        if (!fetchingQuestionGroupSteps && prevFetchingQuestionGroupSteps) {
            if (activeGroupStepCode) {
                history.replace(
                    `/claim${refillClaimId ? '/' + refillClaimId : ''}/${activeGroupStepCode}`,
                );
            } else {
                history.replace(
                    `/claim${refillClaimId ? '/' + refillClaimId : ''}/${questionGroupStepWithSmallestRank ? questionGroupStepWithSmallestRank.code : ''}`,
                );
            }
        }
    }, [
        fetchingQuestionGroupSteps,
        prevFetchingQuestionGroupSteps,
        questionGroupStepWithSmallestRank,
        activeGroupStepCode,
    ]);

    const onSelectChange = (inputChange: { name: string; value: string }) => {
        const name: string = inputChange.name;
        const value = inputChange.value;
        claimTypesStates[name](
            name,
            answers,
            value,
            clearClaimCategoryStateAction,
            clearFormTypesStateAction,
            clearQuestionStateAction,
            changeAnswerStateAction,
            history,
        );
        changeAnswerStateAction({
            name: 'activeGroupStepCode',
            value: '',
        });

        changeAnswerStateAction({
            name: name,
            value: value,
        });
    };

    const handleConfirm = () => {
        //check if the route has change of the modal.
        setHasRouteChangedByModal(true);
        onSelectChange(storedInputChange);
        closeConfirmationModal();
    };

    const closeConfirmationModal = () => {
        setConfirmationModalOpen(false);
        setStoredInputChange(null);
        clearAnswersFromGroupStepsAction(undefined);
    };

    const handleClaimBasicQuestionChange = (evt: {
        target: { name: string; value: string };
    }) => {
        const inputChange = {
            name: evt.target.name,
            value: evt.target.value,
        };
        if (!questionsExists) {
            return onSelectChange(inputChange);
        }
        setStoredInputChange(inputChange);
        setConfirmationModalOpen(true);
    };

    return (
        <Claim
            answers={answers}
            policyTypes={policyTypes}
            claimCategories={claimCategories}
            formTypes={formTypes}
            handleClaimBasicQuestionChange={handleClaimBasicQuestionChange}
            handleConfirm={handleConfirm}
            closeConfirmationModal={closeConfirmationModal}
            confirmationModalOpen={confirmationModalOpen}
            arePoliciesLoading={arePoliciesLoading}
            areClaimCategoriesLoading={areClaimCategoriesLoading}
            areFormTypesLoading={areFormTypesLoading}
            fetchingQuestionGroupSteps={fetchingQuestionGroupSteps}
            setHasRouteChangedByModal={setHasRouteChangedByModal}
            hasRouteChangedByModal={hasRouteChangedByModal}
            refillClaimId={refillClaimId}
            setQuestionGroupsStepsLoading={setQuestionGroupsStepsLoading}
            questionGroupsStepsLoading={questionGroupsStepsLoading}
            prevFetchingQuestionGroupSteps={prevFetchingQuestionGroupSteps}
            loadingNoSubmittedDraft={loadingNoSubmittedDraft}
        />
    );
};

export default ClaimContainer;
